import React, { useState, useMemo } from 'react';
import FilterBar from '../../../Components/Ui/FilterBar';
import TabBar from '../../../Components/Ui/TabBar';
import { useAppNavigate } from '../../../Hooks/back/useAppNavigate';
import { useDispatch, useSelector } from 'react-redux';
import Icon from '../../../Assets/Icon/CustomIcon';
import Badge from '../../Notice/ui/Badge';
import { setDepCd } from '../../../Reducer/SearchReducer';

const TAB_CONFIG = [
  { key: 'ALL', label: '전체', route: null },
  {
    key: 'PERMIT',
    label: '작업허가서',
    route: '/M_Permit/detail',
    needId: true,
  },
  {
    key: 'RESUME',
    label: '작업재개',
    route: '/M_WorkStop/detail',
    needId: true,
  },
  {
    key: 'NEAR_MISS',
    label: '안전신문고',
    route: '/M_WorkInfo',
    needId: false,
  },
  {
    key: 'TPA',
    label: '교육일지',
    route: '/M_PendingApprovals/detail',
    needId: true,
  },
];

export default function M_PEA_BASIC({ rowData1 }) {
  const dispatch = useDispatch();
  const { depList, depCd } = useSelector((state) => state.searchInfo);
  const [activeTab, setActiveTab] = useState('ALL');
  const { navigate } = useAppNavigate();
  const filteredData = useMemo(() => {
    return (rowData1 || []).filter((row) => {
      const matchTab = activeTab === 'ALL' || row.docType === activeTab;
      const matchDep = !depCd || depCd === 'all' || row.reqDeptCd === depCd;
      return matchTab && matchDep;
    });
  }, [rowData1, activeTab, depCd]);

  const filterOptionsByKey = {
    department: [
      { value: 'all', label: '전체' },
      ...(depList || []).map((dep) => ({ value: dep.value, label: dep.name })),
    ],
  };

  const selectedDepLabel =
    depList?.find((d) => d.value === depCd)?.name ?? '전체';

  const filterItems = [
    { key: 'department', label: '부서', value: selectedDepLabel },
  ];

  const handleRowClick = (row) => {
    const tab = TAB_CONFIG.find((t) => t.key === row.docType);

    if (tab?.route) {
      const targetPath = tab.needId ? `${tab.route}/${row.docNo}` : tab.route;

      navigate(targetPath, { state: { row } });
    }
  };

  return (
    <div className="flex h-full flex-col bg-slate-50">
      <div className="border-t border-slate-100 bg-white">
        <TabBar
          items={TAB_CONFIG}
          activeKey={activeTab}
          onTabChange={setActiveTab}
        />
        <div className="px-3">
          <FilterBar
            items={filterItems}
            optionsByKey={filterOptionsByKey}
            onSelect={(key, option) =>
              key === 'department' && dispatch(setDepCd(option.value))
            }
          />
        </div>
      </div>
      {filteredData.length > 0 ? (
        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          {filteredData.map((row) => (
            <div
              key={row.no}
              className="flex cursor-pointer flex-col rounded-lg border border-slate-100 bg-white p-4"
              onClick={() => handleRowClick(row)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {[row.statusTxt, row.uiBadgeType].map((type, idx) => (
                    <Badge
                      key={idx}
                      type={type}
                      label={type}
                      className="rounded-sm! py-3 text-[12px]"
                    />
                  ))}
                </div>

                <p className="text-sm font-semibold text-slate-400">
                  {row.reqDt}
                </p>
              </div>

              <p className="text-md py-3 font-bold">{row.title}</p>

              <div className="flex items-center justify-between text-sm font-semibold text-slate-500">
                <div className="flex items-center gap-1">
                  <Icon.User width="14" height="14" />
                  <p>
                    {row.reqUserNm} | {row.reqDeptNm}
                  </p>
                </div>
                {row.startTime && row.startTime !== '-' && (
                  <div className="flex items-center gap-1">
                    <Icon.Schedule width="14" height="14" />
                    <p>
                      {row.startTime}{' '}
                      {row.endTime !== '-' && `~ ${row.endTime}`}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-1 flex-col items-center justify-center bg-[#F5F5F5] px-4 pb-20 text-center">
          <div className="relative mb-6 flex h-32 w-32 items-center justify-center rounded-full bg-white">
            <div className="aspect-square w-[30vw] max-w-20 text-slate-300">
              <Icon.AssignmentTurned width="100%" height="100%" />
            </div>
            <div className="absolute right-0 bottom-0 flex h-[10vw] max-h-10 w-[10vw] max-w-10 items-center justify-center rounded-full bg-orange-100 text-orange-500">
              <Icon.CheckCircle />
            </div>
          </div>

          <h2 className="mb-3 text-xl font-bold">
            현재 결재 대기 중인 항목이 없습니다.
          </h2>
          <p className="text-sm leading-relaxed whitespace-pre-wrap text-slate-500">
            모든 결재 처리가 완료되었습니다.{'\n'}
            새로운 결재 요청이 생기면 알려드릴게요.
          </p>
        </div>
      )}
    </div>
  );
}
